import { useMemo, useState } from 'react';
import { buildInventoryText, type StockItemView, type StorageSummary } from '@almox/shared';
import { api } from '../api/client.js';
import { compartilharTexto, copiarTexto, podeCompartilhar } from '../lib/share.js';
import { Modal, storageEmoji, useToast } from './ui.js';

type StorageOption = Pick<StorageSummary, 'id' | 'name' | 'icon'> & { itemCount?: number };

interface Props {
  storages: StorageOption[];
  /** Itens ja carregados; sem isso o botao busca o estoque inteiro na hora. */
  items?: StockItemView[];
  title?: string;
  label?: string;
  className?: string;
  /**
   * Pergunta quais dispensas entram antes de copiar. Default: pergunta quando
   * ha mais de uma — com uma so nao ha o que escolher.
   */
  chooser?: boolean;
}

/**
 * Copia o estoque como texto para colar numa conversa (WhatsApp), deixando
 * escolher uma, algumas ou todas as dispensas. Quando o aparelho tem menu de
 * compartilhamento — o app Android sempre tem — oferece mandar direto, sem
 * passar pela area de transferencia.
 *
 * Copiar pode falhar sem aviso (WebView antigo, permissao negada, contexto sem
 * HTTPS). Nesse caso a tela mostra o texto pronto para selecionar a mao, que e
 * pior que copiar sozinho mas melhor que um botao que nao faz nada.
 */
export function ShareStockButton({ storages, items, title, label, className, chooser }: Props) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [textoManual, setTextoManual] = useState<string | null>(null);
  const [escolhendo, setEscolhendo] = useState(false);
  const [carregados, setCarregados] = useState<StockItemView[] | null>(items ?? null);
  const [escolhidas, setEscolhidas] = useState<string[]>(() => storages.map((s) => s.id));

  const perguntar = chooser ?? storages.length > 1;

  function montarTexto(ids: string[], linhas: StockItemView[]): string {
    const selecionadas = storages.filter((storage) => ids.includes(storage.id));
    // Uma dispensa so: o nome dela vira o titulo, que e o que faz sentido na
    // conversa ("Almox — Geladeira").
    const tituloFinal =
      title ??
      (selecionadas.length === 1 && selecionadas[0]
        ? `Almox — ${selecionadas[0].name}`
        : 'Almox — estoque');
    return buildInventoryText(
      selecionadas.map((storage) => ({
        id: storage.id,
        name: storage.name,
        emoji: storageEmoji(storage.icon),
      })),
      linhas.filter((item) => ids.includes(item.storageId)),
      { title: tituloFinal },
    );
  }

  /**
   * Busca o estoque a cada acionamento: guardar a lista entre um clique e outro
   * mandaria uma mensagem desatualizada depois de qualquer entrada ou baixa.
   */
  async function carregarItens(): Promise<StockItemView[]> {
    if (items) return items;
    const linhas = await api.listItems();
    setCarregados(linhas);
    return linhas;
  }

  /** Entrega o texto: compartilhar, copiar, ou mostrar para copiar a mao. */
  async function entregar(texto: string, compartilhar: boolean) {
    if (compartilhar && (await compartilharTexto(texto, 'Almox — estoque'))) return true;
    if (await copiarTexto(texto)) {
      toast('Estoque copiado — e so colar na conversa.', 'ok');
      return true;
    }
    setTextoManual(texto);
    return false;
  }

  async function acionar(compartilhar: boolean) {
    setBusy(true);
    try {
      const linhas = await carregarItens();
      if (perguntar) {
        setEscolhidas(storages.map((storage) => storage.id));
        setEscolhendo(true);
        return;
      }
      await entregar(
        montarTexto(
          storages.map((storage) => storage.id),
          linhas,
        ),
        compartilhar,
      );
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={className ?? 'btn'}
        disabled={busy}
        onClick={() => void acionar(false)}
      >
        {busy ? 'Montando…' : (label ?? '📋 Copiar estoque')}
      </button>
      {!perguntar && podeCompartilhar() ? (
        <button type="button" className="btn" disabled={busy} onClick={() => void acionar(true)}>
          📤 Compartilhar
        </button>
      ) : null}

      {escolhendo && carregados ? (
        <SeletorDeDispensas
          storages={storages}
          items={carregados}
          escolhidas={escolhidas}
          onEscolher={setEscolhidas}
          montarTexto={(ids) => montarTexto(ids, carregados)}
          onEntregar={async (texto, compartilhar) => {
            const foi = await entregar(texto, compartilhar);
            if (foi) setEscolhendo(false);
          }}
          onClose={() => setEscolhendo(false)}
        />
      ) : null}

      {textoManual !== null ? (
        <Modal title="Copie o texto" onClose={() => setTextoManual(null)}>
          <p className="small muted" style={{ marginTop: 0 }}>
            Este aparelho nao deixou copiar sozinho. Toque no texto, selecione tudo e copie.
          </p>
          <textarea
            readOnly
            rows={14}
            value={textoManual}
            onFocus={(evento) => evento.currentTarget.select()}
            style={{ width: '100%' }}
          />
          <div className="modal-actions">
            <button type="button" className="btn" onClick={() => setTextoManual(null)}>
              Fechar
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}

/** Escolha das dispensas que entram na mensagem — uma, algumas ou todas. */
function SeletorDeDispensas({
  storages,
  items,
  escolhidas,
  onEscolher,
  montarTexto,
  onEntregar,
  onClose,
}: {
  storages: StorageOption[];
  items: StockItemView[];
  escolhidas: string[];
  onEscolher: (ids: string[]) => void;
  montarTexto: (ids: string[]) => string;
  onEntregar: (texto: string, compartilhar: boolean) => Promise<void>;
  onClose: () => void;
}) {
  const [enviando, setEnviando] = useState(false);

  const contagem = useMemo(() => {
    const mapa = new Map<string, number>();
    for (const item of items) mapa.set(item.storageId, (mapa.get(item.storageId) ?? 0) + 1);
    return mapa;
  }, [items]);

  const texto = escolhidas.length > 0 ? montarTexto(escolhidas) : '';
  const totalItens = items.filter((item) => escolhidas.includes(item.storageId)).length;
  const todas = escolhidas.length === storages.length;

  function alternar(id: string) {
    onEscolher(
      escolhidas.includes(id) ? escolhidas.filter((atual) => atual !== id) : [...escolhidas, id],
    );
  }

  async function entregar(compartilhar: boolean) {
    setEnviando(true);
    try {
      await onEntregar(texto, compartilhar);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal title="O que entra na mensagem" onClose={onClose}>
      <div className="row" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => onEscolher(todas ? [] : storages.map((storage) => storage.id))}
        >
          {todas ? 'Desmarcar todas' : 'Marcar todas'}
        </button>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() =>
            onEscolher(
              storages
                .filter((storage) => (contagem.get(storage.id) ?? 0) > 0)
                .map((storage) => storage.id),
            )
          }
        >
          So as que tem item
        </button>
      </div>

      <div className="pick-list">
        {storages.map((storage) => {
          const quantos = contagem.get(storage.id) ?? 0;
          return (
            <label key={storage.id} className="check check-line">
              <input
                type="checkbox"
                checked={escolhidas.includes(storage.id)}
                onChange={() => alternar(storage.id)}
              />
              <span style={{ flex: 1 }}>
                {storageEmoji(storage.icon)} {storage.name}
              </span>
              <span className="small muted">
                {quantos === 0 ? 'vazia' : `${quantos} ${quantos === 1 ? 'item' : 'itens'}`}
              </span>
            </label>
          );
        })}
      </div>

      <p className="small muted">
        {escolhidas.length === 0
          ? 'Escolha ao menos uma dispensa.'
          : `${totalItens} ${totalItens === 1 ? 'item' : 'itens'} · ${texto.length} caracteres na mensagem.`}
      </p>

      {escolhidas.length > 0 ? (
        <details className="dicas">
          <summary>Ver como vai ficar</summary>
          <pre className="preview-mensagem">{texto}</pre>
        </details>
      ) : null}

      <div className="modal-actions">
        <button type="button" className="btn" onClick={onClose}>
          Cancelar
        </button>
        {podeCompartilhar() ? (
          <button
            type="button"
            className="btn"
            disabled={enviando || escolhidas.length === 0}
            onClick={() => void entregar(true)}
          >
            📤 Compartilhar
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn-primary"
          disabled={enviando || escolhidas.length === 0}
          onClick={() => void entregar(false)}
        >
          📋 Copiar
        </button>
      </div>
    </Modal>
  );
}
